'use client'

import Link from 'next/link'
import Image from 'next/image'
import { motion } from '@/lib/framer-motion-shim'
import { useI18n } from '@/lib/i18n/context'
import { Pictogram } from '@/components/Pictogram'

/**
 * Carte de projet. Composant navigateur : il ne calcule plus lui-même l'image
 * de couverture ni le score d'impact.
 *
 * Il importait `@/data/odd` (project-photos.json) et `@/data/impact-scoring`
 * (project-scores.json, 1 Mo) : deux catalogues embarqués dans le paquet du
 * navigateur pour afficher une vignette et un pourcentage. L'appelant, côté
 * serveur, fournit désormais `featuredImage` et `impactAverage` (audit
 * performance A1, 20 septembre 2026).
 */

const typeVisual: Record<string, { color: string; icon: string }> = {
  reboisement: { color: 'bg-forest/10 text-forest', icon: 'pine' },
  boisement: { color: 'bg-forest-light/10 text-forest-light', icon: 'tree' },
  grandes_cultures: { color: 'bg-soil/10 text-soil', icon: 'wheat' },
  carbon_agri: { color: 'bg-amber/10 text-amber', icon: 'cow' },
  vergers: { color: 'bg-amber/10 text-amber', icon: 'apple' },
  reduction_intrants: { color: 'bg-sage/50 text-forest', icon: 'droplets' },
  belle_foret: { color: 'bg-forest/10 text-forest', icon: 'pine' },
  haies: { color: 'bg-forest-light/10 text-forest-light', icon: 'leaf' },
  batiment: { color: 'bg-water/10 text-water', icon: 'construction' },
  elevage: { color: 'bg-amber/10 text-amber', icon: 'cow' },
}

interface ProjectCardProps {
  title: string
  slug: string
  type: string
  presentation?: string
  region?: string
  commune?: string
  surface?: number
  co2Tonnes?: number
  availability?: number
  featuredImage?: { url: string; alt?: string }
  index?: number
  /** Score d'impact moyen (0-100), calculé côté serveur. */
  impactAverage?: number
  className?: string
}

export default function ProjectCard({
  title,
  slug,
  type,
  presentation,
  region,
  commune,
  surface,
  co2Tonnes,
  availability,
  featuredImage,
  index = 0,
  impactAverage = 0,
  className,
}: ProjectCardProps) {
  const { t, locale } = useI18n()
  const visual = typeVisual[type] || typeVisual.reboisement!
  const labels = t.typeLabels as Record<string, string>
  const label = labels[type] || type
  const numFmt = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR')
  const href = locale === 'en' ? `/en/projets/${slug}` : `/projets/${slug}`

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className={className}
    >
      <Link href={href} className="card-base block overflow-hidden group h-full flex flex-col" aria-label={`${title} — ${label}${region ? ` — ${region}` : ''}`}>
        {/* Image */}
        <div className="relative h-36 bg-sage overflow-hidden rounded-t-2xl flex-shrink-0">
          {featuredImage?.url ? (
            <Image
              src={featuredImage.url}
              alt={featuredImage.alt || title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-sage text-forest/60">
              <Pictogram name={visual.icon} className="w-10 h-10" />
            </div>
          )}
          {/* Type badge */}
          <span
            className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-white/95 text-gray-800 backdrop-blur-sm shadow-sm"
          >
            <Pictogram name={visual.icon} className="w-3.5 h-3.5" /> {label}
          </span>
          {/* Availability */}
          {typeof availability === 'number' && (
            <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-forest">
              {availability}% {locale === 'en' ? 'available' : 'disponible'}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 flex-1 flex flex-col">
          <h3 className="font-heading font-bold text-lg text-gray-900 group-hover:text-forest transition-colors line-clamp-2">
            {title}
          </h3>
          {(commune || region) && (
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <svg
                aria-hidden="true"
                focusable="false"
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              {commune}
              {commune && region ? ', ' : ''}
              {region}
            </p>
          )}
          {presentation && (
            <p className="text-sm text-gray-500 mt-2 line-clamp-2">{presentation}</p>
          )}

          {/* Metrics */}
          <div className="flex items-center gap-4 mt-auto pt-4 border-t border-gray-50">
            {surface !== undefined && (
              <div className="text-center flex-1">
                <p className="font-heading font-bold text-forest text-sm">
                  {numFmt.format(surface)} ha
                </p>
                <p className="text-[11px] text-gray-500">{t.projets.card.surface}</p>
              </div>
            )}
            {co2Tonnes !== undefined && (
              <div className="text-center flex-1">
                <p className="font-heading font-bold text-forest text-sm">
                  {numFmt.format(co2Tonnes)} t
                </p>
                <p className="text-[11px] text-gray-500">{t.projets.card.co2}</p>
              </div>
            )}
            <div className="text-center flex-1">
              <p className="font-heading font-bold text-sm" style={{ color: impactAverage >= 75 ? '#1B4D3E' : impactAverage >= 60 ? '#D4A24C' : '#6b7280' }}>
                {impactAverage}%
              </p>
              <p className="text-[11px] text-gray-500">{locale === 'en' ? 'Impact' : 'Impact'}</p>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  )
}
